import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Events, setAnalyticsUser } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function formatAuthError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  // null = loading, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(() => localStorage.getItem("sib_access_token") || null);

  const persistToken = (token) => {
    if (token) {
      localStorage.setItem("sib_access_token", token);
    } else {
      localStorage.removeItem("sib_access_token");
    }
    setAccessToken(token);
  };

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem("sib_access_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("sib_access_token");
    if (!token) {
      setUser(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(data);
    } catch (e) {
      persistToken(null);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      persistToken(data.access_token);
      localStorage.setItem("sib_refresh_token", data.refresh_token);
      setUser(data.user);
      Events.login("password");
      setAnalyticsUser(data.user.id, { role: data.user.role || "user" });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name });
      persistToken(data.access_token);
      localStorage.setItem("sib_refresh_token", data.refresh_token);
      setUser(data.user);
      Events.signUp("password");
      setAnalyticsUser(data.user.id, { role: "user" });
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders() });
    } catch (e) {
      // ignore
    }
    Events.logout();
    setAnalyticsUser(null);
    persistToken(null);
    localStorage.removeItem("sib_refresh_token");
    setUser(false);
  };

  const forgotPassword = async (email) => {
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const { data } = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      return { ok: true, message: data.message };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { headers: authHeaders() }
      );
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const deleteAccount = async (password) => {
    try {
      await axios.delete(`${API}/auth/delete-account`, {
        headers: authHeaders(),
        data: { password },
      });
      persistToken(null);
      localStorage.removeItem("sib_refresh_token");
      setUser(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const updateSettings = async (update) => {
    try {
      const { data } = await axios.patch(`${API}/auth/settings`, update, { headers: authHeaders() });
      setUser((u) => (u ? { ...u, ...data } : u));
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatAuthError(e.response?.data?.detail) || e.message };
    }
  };

  const value = {
    user, // null loading, false unauthed, obj authed
    isAuthenticated: !!user && user !== false,
    isLoading: user === null,
    accessToken,
    authHeaders,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    changePassword,
    deleteAccount,
    updateSettings,
    refreshUser: fetchMe,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
